// Datenzugriff der Serienmail. Konvention im Repo: duenne Ladefunktionen ohne eigene
// Logik und ohne Unit-Tests — alles Entscheidbare liegt in den Regel-Modulen daneben.
import { supabase } from '../supabase/client';
import { ermittleDienstleisterKandidaten, ermittleEigentuemerKandidaten, ermittleKontaktKandidaten, ermittleMieterKandidaten, zaehleAusgeblendet } from './empfaenger';
import { baueWerte } from './platzhalter';
import type { Ausgeblendet, DienstleisterRoh, EigentuemerRoh, Filter, Kandidat, KanalZeile, KontaktRoh, MieterRoh, Zielgruppe } from './typen';

export async function ladeObjekte(): Promise<{ id: string; bezeichnung: string; status: string | null }[]> {
  // Fehler NICHT stillschweigend in eine leere Liste verwandeln: bei RLS-Ablehnung
  // oder Netzwerkfehler sieht das sonst genauso aus wie „keine Objekte vorhanden",
  // und der Nutzer bekommt einen falschen Befund statt einer Fehlermeldung.
  const { data, error } = await supabase.from('objekte').select('id, bezeichnung, status').eq('ist_verwaltung', false).order('bezeichnung');
  if (error) throw new Error(error.message);
  return data || [];
}

export async function ladeEinheiten(objektIds: string[]) {
  if (objektIds.length === 0) return [];
  const { data, error } = await supabase
    .from('objekt_einheiten').select('id, objekt_id, bezeichnung')
    .in('objekt_id', objektIds).order('bezeichnung');
  if (error) throw new Error(error.message);
  return data || [];
}

// Abschluss-Review C1: PostgREST liefert ohne .limit() hoechstens 1000 Zeilen und meldet
// das NICHT als Fehler. Ein Verteiler, dem still Bewohner fehlen, ist der schlimmste
// denkbare Ausgang — deshalb ueberall dasselbe Limit wie in den Listenseiten des Projekts
// (MieterListe/ObjekteListe/EigentuemerListe) UND ein harter Abbruch, sobald die
// Trefferzahl das Limit erreicht.
const ZEILEN_LIMIT = 10000;

function pruefeLimit(anzahl: number, was: string): void {
  if (anzahl >= ZEILEN_LIMIT) {
    throw new Error(
      `Zu viele ${was} (${anzahl} oder mehr) — der Verteiler waere unvollstaendig. `
      + 'Bitte die Auswahl auf weniger Objekte einschraenken; lieber ein Abbruch als ein halber Verteiler.',
    );
  }
}

/**
 * Kanaele der gefundenen Kontakte nachladen — eine Abfrage statt einer je Kontakt.
 * Abschluss-Review C2: NICHT alle IDs in EIN .in() — bei ~700 Kandidaten wird der
 * Query-String ~26 KB gross und reisst die URL-Grenze (414 URI Too Long) bzw. laeuft
 * erneut in den 1000-Zeilen-Deckel. Kontakte fielen dann still auf das alte E-Mail-Feld
 * zurueck oder landeten faelschlich auf der Druckliste. Paketweise abfragen — dasselbe
 * Muster wie lib/rechnungen/emailRechnungStatus.ts und lib/dokumente/emailAblageStatus.ts.
 */
async function ladeKanaele(ownerType: 'mieter' | 'eigentuemer' | 'dienstleister', ids: string[]): Promise<KanalZeile[]> {
  if (ids.length === 0) return [];
  const CHUNK = 100;
  const pakete: string[][] = [];
  for (let i = 0; i < ids.length; i += CHUNK) pakete.push(ids.slice(i, i + CHUNK));

  const ergebnisse = await Promise.all(
    pakete.map((paket) =>
      supabase
        .from('kontakt_kanaele').select('owner_id, kanal, wert, ist_primaer, sortierung')
        .eq('owner_type', ownerType).eq('kanal', 'email').in('owner_id', paket)
        .limit(ZEILEN_LIMIT),
    ),
  );

  const zeilen: KanalZeile[] = [];
  for (const { data, error } of ergebnisse) {
    // Fehler NICHT verschlucken: eine fehlende Kanalzeile bedeutet hier stillschweigend
    // „kein oder ein falscher Empfaenger", nicht „nichts passiert".
    if (error) throw new Error(error.message);
    zeilen.push(...((data as KanalZeile[]) || []));
  }
  return zeilen;
}

/**
 * #577: Kandidaten aller gewählten Zielgruppen, nacheinander (Mieter, Eigentümer, Dienstleister,
 * sonstige). `filter.alle` = global: Mieter/Eigentümer aller verwalteten Objekte (ohne V000),
 * Dienstleister und sonstige Kontakte ohne Objektbezug. Doppelte Adressen über Gruppen hinweg
 * entfernt danach teileAuf (eine Mail je Adresse).
 */
export async function ladeKandidaten(
  zielgruppen: Zielgruppe[], filterEin: Filter,
): Promise<{ kandidaten: Kandidat[]; ausgeblendet: number }> {
  if (zielgruppen.length === 0) return { kandidaten: [], ausgeblendet: 0 };
  if (!filterEin.alle && filterEin.objektIds.length === 0) return { kandidaten: [], ausgeblendet: 0 };
  let filter = filterEin;
  if (filter.alle && (zielgruppen.includes('mieter') || zielgruppen.includes('eigentuemer'))) {
    // Global: alle verwalteten Objekte, Einheitenfilter gilt dann nicht.
    const alleObjekte = await ladeObjekte();
    filter = { alle: true, objektIds: alleObjekte.map((o) => o.id), einheitIds: [] };
  }
  // #579: nicht aktive Kontakte (ehemalige Mieter, inaktive Eigentümer, archivierte Objekte …)
  // fallen in empfaenger.ts heraus; hier nur mitzählen, damit der Schritt es anzeigen kann.
  const ausgeblendet: Ausgeblendet = new Set();
  const listen: Kandidat[][] = [];
  for (const g of zielgruppen) listen.push(await ladeGruppe(g, filter, ausgeblendet));
  const kandidaten = listen.flat();
  return { kandidaten, ausgeblendet: zaehleAusgeblendet(ausgeblendet, kandidaten) };
}

async function ladeGruppe(zielgruppe: Zielgruppe, filter: Filter, ausgeblendet: Ausgeblendet): Promise<Kandidat[]> {
  if (zielgruppe === 'dienstleister') return ladeDienstleister(filter, ausgeblendet);
  if (zielgruppe === 'kontakt') return ladeKontakte(filter, ausgeblendet);
  if (filter.objektIds.length === 0) return [];

  if (zielgruppe === 'mieter') {
    // Die Tabelle `mieter` fuehrt KEINE eigenen Adressspalten. Die Postanschrift eines
    // Mieters ist die des OBJEKTS, in dem er wohnt (plus seine Einheit, damit der Brief
    // im richtigen Briefkasten landet) — deshalb kommen strasse/hausnummer/plz/stadt aus
    // dem Objekt-Embed und die Bezeichnung aus dem Einheiten-Embed. Wer hier spaeter
    // `strasse` direkt an `mieter` sucht, bekommt HTTP 400 (Live-Bug 2026-09-04).
    const { data, error } = await supabase
      .from('mieter')
      .select('id, anrede, vorname, nachname, email, status, mietende, objekt_id, einheit_id, '
        + 'objekt:objekte(bezeichnung, status, strasse, hausnummer, plz, stadt), '
        + 'einheit:objekt_einheiten(bezeichnung)')
      .in('objekt_id', filter.objektIds)
      .limit(ZEILEN_LIMIT);
    if (error) throw new Error(error.message);
    // Die Abfrage holt aktive UND ehemalige Mieter (der Statusfilter greift erst danach in
    // ermittleMieterKandidaten) — der Deckel trifft hier also frueher als man denkt.
    pruefeLimit((data || []).length, 'Mieter in der Auswahl');
    const rohe: MieterRoh[] = (data || []).map((m: any) => ({
      id: m.id,
      anrede: m.anrede ?? null,
      vorname: m.vorname,
      nachname: m.nachname,
      email: m.email,
      status: m.status,
      objekt_id: m.objekt_id,
      einheit_id: m.einheit_id,
      objekt_bezeichnung: m.objekt?.bezeichnung ?? null,
      objekt_strasse: m.objekt?.strasse ?? null,
      objekt_hausnummer: m.objekt?.hausnummer ?? null,
      objekt_plz: m.objekt?.plz ?? null,
      objekt_stadt: m.objekt?.stadt ?? null,
      einheit_bezeichnung: m.einheit?.bezeichnung ?? null,
      mietende: m.mietende ?? null,
      objekt_status: m.objekt?.status ?? null,
    }));
    const kanaele = await ladeKanaele('mieter', rohe.map((m) => m.id));
    return ermittleMieterKandidaten(rohe, kanaele, filter, ausgeblendet);
  }

  // Eigentuemer haengen ueber objekt_eigentuemer am Objekt — das ist die eine Wahrheit
  // fuer Eigentum (Commit 7156eaa), NICHT entity_links.
  const { data, error } = await supabase
    .from('objekt_eigentuemer')
    .select('objekt_id, vertrag_bis, objekt:objekte(bezeichnung, status), '
      + 'eigentuemer:eigentuemer(id, anrede, firma_name, vorname, nachname, email, strasse, hausnummer, plz, stadt, status, vertragsende)')
    .in('objekt_id', filter.objektIds)
    .limit(ZEILEN_LIMIT);
  if (error) throw new Error(error.message);
  pruefeLimit((data || []).length, 'Eigentuemer-Zuordnungen in der Auswahl');
  const rohe: EigentuemerRoh[] = (data || [])
    .filter((z: any) => z.eigentuemer)
    .map((z: any) => ({
      ...z.eigentuemer, objekt_id: z.objekt_id, objekt_bezeichnung: z.objekt?.bezeichnung ?? null,
      vertrag_bis: z.vertrag_bis ?? null, objekt_status: z.objekt?.status ?? null,
    }));
  const kanaele = await ladeKanaele('eigentuemer', rohe.map((e) => e.id));
  return ermittleEigentuemerKandidaten(rohe, kanaele, filter, ausgeblendet);
}

export interface SerienmailEntwurf {
  betreff: string;
  html: string;
  vorlageId: string | null;
  anhaenge: { name: string; path: string }[];
  /** #577: alle gewählten Gruppen; in serienmails.zielgruppe steht bei mehreren 'gemischt'. */
  zielgruppen: Zielgruppe[];
  auswahl: Filter;
  /** ISO-Zeitpunkt oder null fuer sofort. */
  geplantAm: string | null;
  absender: { name: string; email: string };
}

/**
 * toLocaleDateString('de-DE') liefert "4.9.2026" ohne fuehrende Nullen — im Rest
 * der App (z. B. formatDatum in SelbstauskunftListe.tsx) wird konsequent zweistellig
 * "04.09.2026" geschrieben. Hier von Hand formatieren, damit die Vorlagenmails dazu passen.
 * Exportiert (statt nur lokal in heuteDeutsch), damit das Format ohne Systemuhr-Mock
 * testbar ist — das Datum steht in jeder verschickten Serienmail.
 */
export const formatiereDatumDeutsch = (d: Date): string => {
  const tag = String(d.getDate()).padStart(2, '0');
  const monat = String(d.getMonth() + 1).padStart(2, '0');
  return `${tag}.${monat}.${d.getFullYear()}`;
};

const heuteDeutsch = () => formatiereDatumDeutsch(new Date());

/**
 * Legt den Lauf und seine Empfaengerzeilen an. Die Platzhalterwerte werden HIER
 * aufgeloest und mitgespeichert — der Worker soll spaeter nichts mehr nachladen
 * muessen, und der Lauf bleibt reproduzierbar, auch wenn sich Stammdaten aendern.
 */
export async function legeSerienmailAn(entwurf: SerienmailEntwurf, erreichbar: Kandidat[]): Promise<string> {
  const { data: lauf, error } = await supabase.from('serienmails').insert({
    betreff: entwurf.betreff,
    html_inhalt: entwurf.html,
    vorlage_id: entwurf.vorlageId,
    anhaenge: entwurf.anhaenge,
    zielgruppe: entwurf.zielgruppen.length === 1 ? entwurf.zielgruppen[0] : 'gemischt',
    zielgruppen: entwurf.zielgruppen,
    auswahl: entwurf.auswahl,
    status: entwurf.geplantAm ? 'geplant' : 'entwurf',
    geplant_am: entwurf.geplantAm,
    erstellt_von: (await supabase.auth.getUser()).data.user?.id ?? null,
  }).select('id').single();
  if (error) throw new Error(error.message);

  const heute = heuteDeutsch();
  const zeilen = erreichbar.map((k) => ({
    serienmail_id: lauf.id,
    owner_type: k.owner_type,
    owner_id: k.owner_id,
    email: k.email as string,
    name: k.name,
    platzhalter: baueWerte(k, entwurf.absender, heute),
  }));
  // In Paketen einfuegen: ein Insert mit hunderten Zeilen laeuft in die Payload-Grenze.
  for (let i = 0; i < zeilen.length; i += 200) {
    const { error: err } = await supabase.from('serienmail_empfaenger').insert(zeilen.slice(i, i + 200));
    if (err) {
      // Lieber gar kein Lauf als ein Lauf mit halbem Verteiler: den angelegten Lauf
      // wieder abraeumen (Empfaengerzeilen haengen per ON DELETE CASCADE dran und
      // verschwinden mit), sonst bleibt eine Karteileiche stehen, die spaeter
      // versehentlich gestartet werden koennte. Der Aufraeumversuch selbst darf den
      // eigentlichen Fehler nicht verdecken, falls er ebenfalls scheitert.
      try {
        await supabase.from('serienmails').delete().eq('id', lauf.id);
      } catch {
        // bewusst verschluckt — der urspruengliche Fehler ist der relevante
      }
      throw new Error(err.message);
    }
  }
  return lauf.id as string;
}

export async function starteVersand(serienmailId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('serienmail-versand', { body: { serienmail_id: serienmailId } });
  if (error) throw new Error(error.message);
}

/** „Fehlgeschlagene erneut senden": nur die Fehlerzeilen zuruecksetzen, Zaehler auf 0. */
export async function setzeFehlerZurueck(serienmailId: string): Promise<void> {
  const { error } = await supabase.from('serienmail_empfaenger')
    .update({ status: 'offen', versand_versuche: 0, fehler_text: null })
    .eq('serienmail_id', serienmailId).eq('status', 'fehler');
  if (error) throw new Error(error.message);
}

const DL_SPALTEN = 'id, name, email, aktiv, strasse, hausnummer, plz, stadt';
async function ladeDienstleister(filter: Filter, ausgeblendet: Ausgeblendet): Promise<Kandidat[]> {
  let rohe: DienstleisterRoh[];
  if (filter.alle) {
    const { data, error } = await supabase.from('dienstleister').select(DL_SPALTEN).limit(ZEILEN_LIMIT);
    if (error) throw new Error(error.message);
    pruefeLimit((data || []).length, 'Dienstleister');
    rohe = (data || []).map((d: any) => ({ ...d, objekt_id: null, objekt_bezeichnung: null }));
  } else {
    if (filter.objektIds.length === 0) return [];
    // Zuordnung Objekt ↔ Dienstleister: dienstleister_objekt (nur aktive Verknüpfungen)
    const { data, error } = await supabase
      .from('dienstleister_objekt')
      .select(`objekt_id, bis, objekt:objekte(bezeichnung, status), dienstleister:dienstleister(${DL_SPALTEN})`)
      .in('objekt_id', filter.objektIds).eq('aktiv', true)
      .limit(ZEILEN_LIMIT);
    if (error) throw new Error(error.message);
    pruefeLimit((data || []).length, 'Dienstleister-Zuordnungen in der Auswahl');
    rohe = (data || []).filter((z: any) => z.dienstleister)
      .map((z: any) => ({
        ...z.dienstleister, objekt_id: z.objekt_id, objekt_bezeichnung: z.objekt?.bezeichnung ?? null,
        bis: z.bis ?? null, objekt_status: z.objekt?.status ?? null,
      }));
  }
  const kanaele = await ladeKanaele('dienstleister', rohe.map((d) => d.id));
  return ermittleDienstleisterKandidaten(rohe, kanaele, filter, ausgeblendet);
}

const KONTAKT_SPALTEN = 'id, anrede, firma_name, vorname, nachname, email, status, strasse, hausnummer, plz, stadt';
async function ladeKontakte(filter: Filter, ausgeblendet: Ausgeblendet): Promise<Kandidat[]> {
  let rohe: KontaktRoh[];
  if (filter.alle) {
    const { data, error } = await supabase.from('kontakte').select(KONTAKT_SPALTEN).limit(ZEILEN_LIMIT);
    if (error) throw new Error(error.message);
    pruefeLimit((data || []).length, 'sonstige Kontakte');
    rohe = (data || []).map((k: any) => ({ ...k, objekt_id: null, objekt_bezeichnung: null }));
  } else {
    if (filter.objektIds.length === 0) return [];
    // objekt_kontakte.objekt_id hat keinen Fremdschlüssel auf objekte → kein Embed möglich,
    // Objektnamen aus ladeObjekte nachschlagen.
    const [{ data, error }, objekte] = await Promise.all([
      supabase.from('objekt_kontakte').select(`objekt_id, kontakt:kontakte(${KONTAKT_SPALTEN})`)
        .in('objekt_id', filter.objektIds).limit(ZEILEN_LIMIT),
      ladeObjekte(),
    ]);
    if (error) throw new Error(error.message);
    pruefeLimit((data || []).length, 'Kontakt-Zuordnungen in der Auswahl');
    const namen = new Map(objekte.map((o) => [o.id, o]));
    rohe = (data || []).filter((z: any) => z.kontakt)
      .map((z: any) => ({
        ...z.kontakt, objekt_id: z.objekt_id,
        objekt_bezeichnung: namen.get(z.objekt_id)?.bezeichnung ?? null, objekt_status: namen.get(z.objekt_id)?.status ?? null,
      }));
  }
  return ermittleKontaktKandidaten(rohe, filter, ausgeblendet);
}
